import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import LayerDriftEntryRow from "@/v2-backoffice-client/layer/drift/LayerDriftEntryRow"
import LayerDriftSeverityBadge from "@/v2-backoffice-client/layer/drift/LayerDriftSeverityBadge"
import {
  groupBySeverity,
  pluralChanges,
  type LayerDriftEntry,
  type SeverityLabel,
} from "@/v2-backoffice-client/layer/drift/layer.drift.utils"

export type LayerDriftTableProps = ComponentProps<"div"> & {
  entries: LayerDriftEntry[]
}

// LayerDriftTable groups the raw diff entries by severity and renders
// one section per non-empty bucket. Sections always appear in the
// (breaking, warning, info) order so the most impactful changes are
// the first thing the operator's eye lands on. Severity lives on the
// section header alone -- repeating it per entry would put a third
// colour next to the diff's red and green.
function LayerDriftTable(props: LayerDriftTableProps) {
  const { entries, className, ...divProps } = props

  const groups = groupBySeverity(entries)

  return (
    <div className={cn("flex flex-col gap-8", className)} {...divProps}>
      {groups.map(({ severity, entries: groupEntries }) => (
        <Section key={severity} severity={severity} entries={groupEntries} />
      ))}
    </div>
  )
}

function Section(props: {
  severity: SeverityLabel
  entries: LayerDriftEntry[]
}) {
  const { severity, entries } = props
  return (
    <section className="layer-drift-section">
      <header className="layer-drift-section-head">
        <LayerDriftSeverityBadge severity={severity}>
          {severity}
        </LayerDriftSeverityBadge>
        <span className="layer-drift-section-count">
          {pluralChanges(entries.length)}
        </span>
      </header>
      <div className="layer-drift-section-list">
        {entries.map((e, i) => (
          <LayerDriftEntryRow key={`${e.Path}-${i}`} entry={e} />
        ))}
      </div>
    </section>
  )
}

export default LayerDriftTable
